import React, { Suspense } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { theme } from '../../styles/theme';

const panelStyle: React.CSSProperties = {
  padding: '32px 24px',
  background: theme.colors.bgPrimary,
  color: theme.colors.textPrimary,
  fontFamily: theme.fonts.primary,
  lineHeight: 1.5,
};

class PageErrorBoundary extends React.Component<
  { children: React.ReactNode; resetKey: string },
  { failed: boolean; resetKey: string }
> {
  state = { failed: false, resetKey: this.props.resetKey };

  static getDerivedStateFromProps(
    props: { resetKey: string },
    state: { resetKey: string }
  ) {
    return props.resetKey !== state.resetKey
      ? { failed: false, resetKey: props.resetKey }
      : null;
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) {
      return (
        <section role="alert" aria-label="Page loading error" style={panelStyle}>
          <h1 style={{ margin: '0 0 8px', fontSize: '24px' }}>This page couldn’t load.</h1>
          <p style={{ margin: '0 0 16px' }}>
            Check your connection, then reload this page to try again.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              minHeight: '44px',
              padding: '8px 16px',
              marginRight: '16px',
              background: theme.colors.bgElevated,
              color: theme.colors.textPrimary,
              border: `1px solid ${theme.colors.borderLight}`,
              borderRadius: '8px',
              font: 'inherit',
              cursor: 'pointer',
            }}
          >
            Reload page
          </button>
          <Link to="/" style={{ color: theme.colors.textPrimary }}>Back to homepage</Link>
        </section>
      );
    }

    return this.props.children;
  }
}

/** Keep route loading/errors inside guards and, for admin children, inside the layout. */
export function RoutePage({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  return (
    <PageErrorBoundary resetKey={location.key}>
      <Suspense fallback={<div role="status" style={panelStyle}>Loading page…</div>}>
        {children}
      </Suspense>
    </PageErrorBoundary>
  );
}
